import { useTranslation } from 'react-i18next';

const steps = [
  {
    icon: 'ri-search-line',
    title: 'Passaggio 1',
    subtitle: 'Ricerca del prodotto',
    desc: 'Il primo passo consiste nell\'individuare il prodotto desiderato. È importante analizzare: nome prodotto, categoria, caratteristiche e prezzo.',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Passaggio 2',
    subtitle: 'Analisi delle informazioni',
    desc: 'Dopo aver trovato un prodotto è necessario verificare tutti i dettagli disponibili: descrizione, immagini, varianti e disponibilità.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Passaggio 3',
    subtitle: 'Controllo qualità QC',
    desc: 'Uno degli aspetti più importanti riguarda il controllo qualità. Le immagini QC aiutano a comprendere condizioni del prodotto, dettagli estetici ed eventuali problemi.',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Passaggio 4',
    subtitle: 'Confronto prodotti',
    desc: 'Prima della scelta finale è consigliabile confrontare più alternative considerando prezzo, qualità, informazioni e spedizione.',
  },
  {
    icon: 'ri-check-double-line',
    title: 'Passaggio 5',
    subtitle: 'Decisione finale',
    desc: 'Dopo aver analizzato tutte le informazioni l\'utente può procedere con maggiore sicurezza.',
  },
];

const safetyIssues = [
  { problem: 'Informazioni incomplete', solution: 'Analizzare tutti i dettagli' },
  { problem: 'Differenza prodotto reale/descritto', solution: 'Controllare immagini QC' },
  { problem: 'Costi inattesi', solution: 'Verificare prezzo totale' },
  { problem: 'Problemi spedizione', solution: 'Utilizzare tracking' },
];

export default function FeatureGuide() {
  const { t } = useTranslation();

  return (
    <>
      <section className="py-16 md:py-24 bg-background-50 px-4 md:px-6">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full border border-background-300 text-foreground-600 text-xs font-medium mb-4">
              Guide &amp; Tutorial
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
              {t('section3.title')}
            </h2>
            <p className="text-foreground-500 text-sm md:text-base">{t('section3.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {steps.map((step, idx) => (
              <div
                key={idx}
                className="group bg-background-100 rounded-2xl p-6 hover:bg-accent-50/60 transition-colors duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-background-200 text-accent-500 mb-4 group-hover:bg-accent-100 group-hover:text-accent-600 transition-colors">
                  <i className={`${step.icon} text-xl`}></i>
                </div>
                <span className="text-xs text-accent-500 font-semibold">{step.title}</span>
                <h3 className="font-semibold text-foreground-800 mt-1 mb-2">{step.subtitle}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-background-100 px-4 md:px-6">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {t('section4.title')}
            </h2>
            <p className="text-foreground-500 text-sm italic max-w-2xl mx-auto">{t('section4.subtitle')}</p>
            <p className="text-foreground-600 text-sm mt-3">{t('section4.desc')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <h3 className="font-semibold text-foreground-800 mb-5">Consigli di sicurezza</h3>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className="ri-image-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-800">Controllare sempre le informazioni prodotto</p>
                    <p className="text-xs text-foreground-500 mt-0.5">Prima di acquistare verificare immagini, descrizione e caratteristiche.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className="ri-money-euro-circle-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-800">Analizzare il prezzo completo</p>
                    <p className="text-xs text-foreground-500 mt-0.5">Considerare prezzo prodotto, eventuali costi aggiuntivi e spedizione.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className="ri-lock-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-800">Utilizzare metodi affidabili</p>
                    <p className="text-xs text-foreground-500 mt-0.5">Proteggere sempre dati personali, informazioni pagamento e account.</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-foreground-800 mb-5">Tabella sicurezza</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-background-300">
                      <th className="text-left py-2.5 pr-4 font-semibold text-foreground-800">Possibile problema</th>
                      <th className="text-left py-2.5 font-semibold text-foreground-800">Soluzione consigliata</th>
                    </tr>
                  </thead>
                  <tbody>
                    {safetyIssues.map((item, idx) => (
                      <tr key={idx} className="border-b border-background-200">
                        <td className="py-2.5 pr-4 text-foreground-600">{item.problem}</td>
                        <td className="py-2.5 text-accent-600 font-medium">{item.solution}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}